/**
 * Тарифы подписки Favbox — единый источник цен.
 * Используется лендингом (блок тарифов), публичной офертой и платёжным кодом.
 * Менять цену — только здесь; тексты на страницах подтянутся сами.
 *
 * Архитектура регионов: цены и платёжный провайдер зависят от региона юзера.
 * Сейчас регион один — 'ru' (рубли, Robokassa). Для нероссийских юзеров в
 * будущем добавляется второй прайс-лист (валюта + провайдер) в PRICE_LISTS и
 * ветка в resolvePaymentRegion (решение по языку интерфейса/локали) — больше
 * ничего менять не нужно: страница подписки и платёжный код читают отсюда.
 *
 * Цены RU в рублях, НДС не облагаются (исполнитель — плательщик НПД).
 */

export type PaymentRegion = 'ru'; // будущее: | 'intl'
export type PaymentProvider = 'robokassa'; // будущее: | 'paddle' | ...

export interface SubscriptionPlan {
  /** Идентификатор плана — пойдёт в платёжные ссылки и БД */
  id: 'monthly' | 'yearly';
  /** Цена за период в валюте прайс-листа */
  priceRub: number;
  /** Длительность оплаченного периода в днях */
  periodDays: number;
  /** Название периода для интерфейса */
  periodLabel: string;
}

export interface PriceList {
  region: PaymentRegion;
  provider: PaymentProvider;
  /** ISO-код валюты прайс-листа */
  currency: 'RUB';
  plans: Record<'monthly' | 'yearly', SubscriptionPlan>;
}

export const SUBSCRIPTION_PLANS: Record<'monthly' | 'yearly', SubscriptionPlan> = {
  monthly: { id: 'monthly', priceRub: 199, periodDays: 30, periodLabel: 'месяц' },
  yearly: { id: 'yearly', priceRub: 1490, periodDays: 365, periodLabel: 'год' },
};

export const PRICE_LISTS: Record<PaymentRegion, PriceList> = {
  ru: { region: 'ru', provider: 'robokassa', currency: 'RUB', plans: SUBSCRIPTION_PLANS },
};

/**
 * Регион оплаты для юзера. Сейчас всегда 'ru' (интерфейс только русский).
 * Когда появится локализация — решаем по языку интерфейса юзера (locale),
 * сюда добавится ветка 'intl'.
 */
export function resolvePaymentRegion(_locale?: string | null): PaymentRegion {
  return 'ru';
}

export function getPriceList(region: PaymentRegion = 'ru'): PriceList {
  return PRICE_LISTS[region];
}

export type SubscriptionPlanId = SubscriptionPlan['id'];

export function isValidPlanId(value: unknown): value is SubscriptionPlanId {
  return value === 'monthly' || value === 'yearly';
}

/** Сколько стоит месяц при оплате за год (округление до рубля) */
export function yearlyPerMonthRub(): number {
  return Math.round(SUBSCRIPTION_PLANS.yearly.priceRub / 12);
}

/** Экономия за год против помесячной оплаты, в рублях */
export function yearlySavingsRub(): number {
  return SUBSCRIPTION_PLANS.monthly.priceRub * 12 - SUBSCRIPTION_PLANS.yearly.priceRub;
}

/** Сколько целых «месяцев в подарок» даёт годовая оплата (для маркетинговой строки) */
export function yearlyFreeMonths(): number {
  return Math.floor(yearlySavingsRub() / SUBSCRIPTION_PLANS.monthly.priceRub);
}
